import React, { useState, useEffect, useContext, useCallback } from "react"
import {
    Text,
    View,
    StyleSheet,
    ScrollView,
} from "react-native"

import { AuthContext } from '../../contexts/auth/Store';

import { AnalyticsService } from "../../constants/Analytics";
import Colors from "../../constants/Colors";
import ProductService from "../../services/ProductService";
import GenericHeader from '../../components/headers/GenericHeader';
import config from "../../constants/Config";
import { GetPricetag, SafeJSONObject } from "../../constants/Utils";
import { unionBy } from "lodash";
import CategoryService from "../../services/CategoryService";
import { IProduct, IProductPagedData } from "../../interfaces/product";
import { ICategory } from "../../interfaces/category";
import { useFocusEffect, useNavigation, useNavigationState, useRoute } from "@react-navigation/native";
import ProductStack from "../../components/product/ProductStack";
import AsyncStorage from "@react-native-async-storage/async-storage";
import NextPageLoaderGeneric from "../../components/common/NextPageLoaderGeneric";
import useMounted from "../../hooks/useMounted";
import EmptyView from "../../components/common/EmptyView";

const analytics = new AnalyticsService();
const productService = new ProductService();
const categoryService = new CategoryService();

function CategoryProductScreen() {
    const route = useRoute();
    const navigation = useNavigation();
    const isMounted = useMounted()

    const auth: any = useContext(AuthContext)
    const buyer = auth.getUserData.buyerData

    const [isLoading, setLoading] = useState(true);
    const [loadingNext, setLoadingNext] = useState(false)

    const [category, setCategory]: [ICategory | null, any] = useState(null)
    const [state, setState]: [IProductPagedData, any] = useState({
        page: 0,
        limit: config.pagination_limit,
        total: 1,
        count: 0,
        data: []
    })

    const fetch_category_products = async (_id: string) => {
        let _buyer = await AsyncStorage.getItem('buyerData');
        _buyer = SafeJSONObject(_buyer)
        if (!state.data.length) {
            isMounted.current && setLoading(true)
        }
        if (state.page < state.total) {
            productService.fetchAvailableByCategory(
                _id,
                _buyer?.location_id,
                state.page + 1,
                state.limit
            )
                .then((res: IProductPagedData) => {
                    let sorted_data = res.data.filter((product: IProduct) => {
                        let pricetag = GetPricetag(product)
                        if (pricetag && product.status === 'active') {
                            product.pricetag = pricetag
                            return pricetag
                        } else {
                            return null
                        }
                    })
                    isMounted.current && setState((prev: IProductPagedData) => {
                        return {
                            ...res,
                            data: unionBy(prev.data, sorted_data, '_id')
                        }
                    })
                    isMounted.current && setLoading(false)
                    isMounted.current && setLoadingNext(false)
                })
                .catch(err => {
                    console.log(err)
                    isMounted.current && setLoading(false)
                    isMounted.current && setLoadingNext(false)
                })
        } else {
            isMounted.current && setLoading(false)
            isMounted.current && setLoadingNext(false)
        }
    }

    const fetchNextPage = () => {
        isMounted.current && setLoadingNext(true)
        if (category) {
            fetch_category_products(category._id)
        }
    }

    const initialize = (id: string) => {
        isMounted.current && setLoading(true)
        categoryService
            .fetchById(id)
            .then(res => {
                analytics.logEvent('Viewed Category Products', auth.getUserData.buyerData, { category_data: res })
                analytics.googleHelpers.setCurrentScreen('ViewedCategoryProducts', 'ViewedCategoryProducts')

                navigation.setOptions({
                    title: res.name + ' Products - Suplias Now'
                })

                const filtered_products = res.products.filter((product: IProduct) => {
                    let pricetag = GetPricetag(product)
                    if (pricetag && product.status === 'active') {
                        product.pricetag = pricetag
                        return pricetag
                    } else {
                        return null
                    }
                })

                isMounted.current && setCategory(res)
                if (res?.products?.length) {
                    isMounted.current && setState((prev: IProductPagedData) => {
                        return {
                            ...res,
                            page: Math.ceil(res.products.length / config.pagination_limit),
                            count: res.products.length,
                            data: unionBy(prev.data, filtered_products, '_id')
                        }
                    })

                    isMounted.current && setLoading(false)
                    isMounted.current && setLoadingNext(false)
                } else {
                    fetch_category_products(id)
                }
            })
            .catch(err => {
                console.log(err)
                nav_back()
            })
    }

    const nav_back = () => {
        navigation.navigate('HomeTab', {
            screen: 'Category'
        })
    }

    useFocusEffect(
        useCallback(() => {
            // the screen is focused, take actions
            if (!route.params?.id) {
                nav_back()
            } else {
                initialize(route.params?.id)
            }

            return () => {
                // screen is blurred, take actions
                isMounted && setCategory(null)
                isMounted && setLoading(true)
                isMounted && setState({
                    page: 0,
                    limit: config.pagination_limit,
                    total: 1,
                    count: 0,
                    data: []
                })
            };
        }, [route])
    );


    return <>
        <GenericHeader
            title={`${category && !isLoading ? category.name + ' Products' : 'Category Products'}`}
            backAction={nav_back}
        />
        <View style={styles.container}>
            {
                state.count > 0
                    ? <ProductStack
                        products={state.data}
                        isLoading={isLoading}
                        showHeading={true}
                        onEndReached={fetchNextPage}
                        footerComponent={() => <NextPageLoaderGeneric loading={loadingNext} page={state.page} total={state.total} action={fetchNextPage} />}
                    />
                    : isLoading
                        ? <ProductStack
                            products={state.data}
                            isLoading={isLoading}
                            showHeading={true}
                            onEndReached={fetchNextPage}
                            footerComponent={() => <NextPageLoaderGeneric loading={loadingNext} page={state.page} total={state.total} action={fetchNextPage} />}
                        />
                        : <EmptyView
                            summary="There are no products under this category. Check back another time."
                            actionTitle="See other categories"
                            action={nav_back}
                        />
            }
        </View>
    </>
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        justifyContent: 'flex-start',
        backgroundColor: Colors.shades._5
    },
})

export default CategoryProductScreen
